import { isDefined } from "@clipboard-health/util-ts";
import { FormControl, FormHelperText, FormLabel, type SxProps, type Theme } from "@mui/material";
import { type ReactNode } from "react";
import {
  type Control,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form";

interface FormFieldControllerProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues>,
> {
  name: TName;
  control: Control<TFormValues>;
  label?: ReactNode;
  hideLabel?: boolean;
  helperText?: string;
  hideHelperText?: boolean;
  render: (field: ControllerRenderProps<TFormValues, TName>) => ReactNode;
  required?: boolean;
  sx?: SxProps<Theme>;
  disabled?: boolean;
}

export function FormFieldController<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues>,
>(props: FormFieldControllerProps<TFormValues, TName>) {
  const {
    name,
    control,
    label,
    hideLabel = false,
    hideHelperText = false,
    helperText,
    render,
    required,
    sx,
    disabled,
  } = props;
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <FormControl fullWidth sx={sx} disabled={disabled}>
      {label && (
        <FormLabel htmlFor={name} hidden={hideLabel} required={required}>
          {label}
        </FormLabel>
      )}
      {render(field)}
      <FormHelperText
        hidden={hideHelperText && !error?.message}
        sx={{
          height: "1rem",
          marginLeft: 0,
          visibility: (helperText ?? error?.message) ? "visible" : "hidden",
        }}
        error={isDefined(error)}
      >
        {error?.message ?? helperText ?? ""}
      </FormHelperText>
    </FormControl>
  );
}
